import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { detailPanelMoved } from './useDetailPanel';

const readParam = (search, param, type) => {
  const raw = new URLSearchParams(search).get(param);
  if (!raw) return null;
  if (type !== 'number') return raw;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Take the id a link to this page names out of the address (`/tags?tag_id=5`
 * → 5), or null. A phone page opens that record's sheet with it (MI-343). Out
 * of the address, the sheet's history entry sits on the plain page and a back
 * swipe lands on the list. Run once the page's list is in: a second call finds
 * nothing.
 */
export function takeLinkedId(param) {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(param)) return null;
  const id = readParam(url.search, param, 'number');
  url.searchParams.delete(param);
  window.history.replaceState(window.history.state, '', url);
  return id;
}

export function useUrlSyncedId(param, { type = 'number', fallback = null } = {}) {
  const [id, setId] = useState(() => (
    readParam(window.location.search, param, type)
    ?? (typeof fallback === 'function' ? fallback() : fallback)
  ));

  // A link to a record followed while its page is already open (a chip in the
  // floating chat, MI-343): the id arrives in the address, and the first
  // render is long past. Read from the router's location, which only moves on
  // a navigation, never on the `replaceState` below.
  const location = useLocation();
  const followed = useRef(location.key);
  useEffect(() => {
    if (followed.current === location.key) return;
    followed.current = location.key;
    const linked = readParam(location.search, param, type);
    if (linked != null) setId(linked);
  }, [location.key, location.search, param, type]);

  useEffect(() => {
    const url = new URL(window.location);
    const shouldSet = type === 'number' ? typeof id === 'number' : Boolean(id);
    if (shouldSet) {
      url.searchParams.set(param, id);
    } else {
      url.searchParams.delete(param);
    }
    window.history.replaceState({}, '', url);
    detailPanelMoved();
  }, [id, param, type]);

  return [id, setId];
}
